import { useContext, useMemo } from 'react';

import { XYChartDataContext } from '../context/XYChartData.context.js';
import {
  BAR_VARIANT,
  type BarSeriesToMetadata,
  type BarSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';
import { getGroupedAxisCoordinates } from '../utils/get-grouped-axis-coordinates.js';

export interface BarDataInternal {
  filteredSeriesWithDatapoints: BarSeriesWithDatapoints[];
  seriesToMetadata: BarSeriesToMetadata;
  /* Indicates whether the x-axis has grouped series */
  hasXGroups: boolean;
  /* Indicates whether the y-axis has grouped series */
  hasYGroups: boolean;
}

export const useBarData = () => {
  const context = useContext(XYChartDataContext);

  if (context === undefined) {
    throw new Error('useBarData must be used within a XYChartDataContext');
  }

  const filteredContext = useMemo(() => {
    const groupedAxisCoordinates = getGroupedAxisCoordinates(
      context.axisIdToDimension,
      context.axes,
    );

    return {
      seriesToMetadata: context.seriesToMetadata,
      filteredSeriesWithDatapoints: context.filteredSeriesWithDatapoints.filter(
        (series) => series.type === BAR_VARIANT,
      ),
      hasXGroups: groupedAxisCoordinates.has('x'),
      hasYGroups: groupedAxisCoordinates.has('y'),
    };
  }, [context]);

  return filteredContext as BarDataInternal;
};
